import { FC, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

import { useAuth } from '../../../../contexts/AuthContext';
import Config from '../../../../lib/config';
import { DEFAULT_COLORS } from '../../../lib/colors';
import { useUpdateProviderMutation } from '../../../../generated/graphql';

import ToastAlert from '../../../components/ToastAlert';

import WidgetStylePicker, { WidgetStyle } from './WidgetStylePicker';
import WidgetPreview from './WidgetPreview';
import EmbedCodeSnippet from './EmbedCodeSnippet';

const ClientPortalSection: FC = () => {
  const { authedProviderUser, refreshAuthedProviderUser } = useAuth();
  const [widgetStyle, setWidgetStyle] = useState<WidgetStyle>('card');

  const provider = authedProviderUser?.provider;
  const providerId = provider?.id || '';
  const providerName = provider?.name || '';
  const primaryColor =
    authedProviderUser?.primaryColor || DEFAULT_COLORS.PRIMARY;
  const squareLogoUrl = authedProviderUser?.squareLogoMedia?.url || null;

  const calendlyConnected = !!provider?.calendlyIntegration?.isEnabled;
  const servicesEnabled = !!provider?.clientPortalBookServicesEnabled;
  const calendlyEnabled = !!provider?.clientPortalBookCalendlyEnabled;
  const assessmentTrendsEnabled =
    !!provider?.clientPortalAssessmentTrendsEnabled;

  const baseUrl = Config.REACT_APP_WEB_APP_URL || window.location.origin;

  const [updateProvider, { loading: updating }] = useUpdateProviderMutation();

  const showError = () => {
    toast.custom(({ visible }) => (
      <ToastAlert
        isVisible={visible}
        message="Could not save setting. Please try again."
        level="error"
      />
    ));
  };

  const showSuccess = (message: string) => {
    toast.custom(({ visible }) => (
      <ToastAlert isVisible={visible} message={message} level="success" />
    ));
  };

  const onToggleServices = async (next: boolean) => {
    try {
      await updateProvider({
        variables: { input: { clientPortalBookServicesEnabled: next } },
      });
      await refreshAuthedProviderUser?.();
      showSuccess(
        next
          ? 'Service booking enabled in Client Portal'
          : 'Service booking disabled in Client Portal',
      );
    } catch {
      showError();
    }
  };

  const onToggleCalendly = async (next: boolean) => {
    try {
      await updateProvider({
        variables: { input: { clientPortalBookCalendlyEnabled: next } },
      });
      await refreshAuthedProviderUser?.();
      showSuccess(
        next
          ? 'Calendly booking enabled in Client Portal'
          : 'Calendly booking disabled in Client Portal',
      );
    } catch {
      showError();
    }
  };

  const onToggleAssessmentTrends = async (next: boolean) => {
    try {
      await updateProvider({
        variables: { input: { clientPortalAssessmentTrendsEnabled: next } },
      });
      await refreshAuthedProviderUser?.();
      showSuccess(
        next
          ? 'Assessment history enabled in Client Portal'
          : 'Assessment history disabled in Client Portal',
      );
    } catch {
      showError();
    }
  };

  return (
    <div className="flex w-full flex-col gap-y-12 pl-[1px] pt-6 pb-2">
      <div>
        <div className="mb-4 text-category font-medium">Booking options</div>
        <div className="mb-6 text-caption text-neutral-125">
          Choose what your clients can book directly from the Client Portal.
        </div>

        <div className="flex flex-col gap-y-4 pl-2">
          <div className="flex items-start">
            <input
              id="clientPortalBookServicesEnabled"
              type="checkbox"
              disabled={updating}
              checked={servicesEnabled}
              onChange={(e) => onToggleServices(e.target.checked)}
              className="mt-1 mr-3 h-5 w-5 flex-shrink-0 cursor-pointer rounded border-neutral-100 text-secondary-125 focus:ring-secondary-125 disabled:cursor-not-allowed disabled:opacity-60"
            />
            <div>
              <label
                htmlFor="clientPortalBookServicesEnabled"
                className="cursor-pointer"
              >
                <div className="text-caption font-medium text-green-150">
                  Services
                </div>
                <div className="text-small-caption text-neutral-110">
                  Enables a "Book a service" button that lists your bookable
                  services to existing clients.
                </div>
              </label>
              <div className="mt-1 text-small-caption text-neutral-110">
                <Link
                  to="/services"
                  className="text-secondary-125 underline hover:text-secondary-150"
                >
                  Manage Services
                </Link>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <input
              id="clientPortalBookCalendlyEnabled"
              type="checkbox"
              disabled={updating || !calendlyConnected}
              checked={calendlyConnected && calendlyEnabled}
              onChange={(e) => onToggleCalendly(e.target.checked)}
              className="mt-1 mr-3 h-5 w-5 flex-shrink-0 cursor-pointer rounded border-neutral-100 text-secondary-125 focus:ring-secondary-125 disabled:cursor-not-allowed disabled:opacity-60"
            />
            <div>
              <label
                htmlFor="clientPortalBookCalendlyEnabled"
                className={
                  calendlyConnected ? 'cursor-pointer' : 'cursor-not-allowed'
                }
              >
                <div className="text-caption font-medium text-green-150">
                  Calendly events
                </div>
                <div className="text-small-caption text-neutral-110">
                  {calendlyConnected
                    ? 'Enables a "Book a session" button that lists your Calendly events to existing clients.'
                    : 'Connect Calendly to enable this option.'}
                </div>
              </label>
              {calendlyConnected && (
                <div className="mt-1 text-small-caption text-neutral-110">
                  <Link
                    to="/integrations/calendly"
                    className="text-secondary-125 underline hover:text-secondary-150"
                  >
                    Manage Calendly events
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="mb-4 text-category font-medium">Assessments</div>
        <div className="mb-6 text-caption text-neutral-125">
          Choose what completed assessments look like for clients.
        </div>

        <div className="flex flex-col gap-y-4 pl-2">
          <div className="flex items-start">
            <input
              id="clientPortalAssessmentTrendsEnabled"
              type="checkbox"
              disabled={updating}
              checked={assessmentTrendsEnabled}
              onChange={(e) => onToggleAssessmentTrends(e.target.checked)}
              className="mt-1 mr-3 h-5 w-5 flex-shrink-0 cursor-pointer rounded border-neutral-100 text-secondary-125 focus:ring-secondary-125 disabled:cursor-not-allowed disabled:opacity-60"
            />
            <div>
              <label
                htmlFor="clientPortalAssessmentTrendsEnabled"
                className="cursor-pointer"
              >
                <div className="text-caption font-medium text-green-150">
                  Show assessment history in Client Portal
                </div>
                <div className="text-small-caption text-neutral-110">
                  Lets clients see their scores over time when they have
                  completed the same assessment more than once. Clinical
                  interpretation stays with you.
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="flex w-full flex-wrap gap-x-14 gap-y-12">
        <div className="min-w-[400px] flex-[2]">
          <div className="mb-4 text-category font-medium">
            Embeddable Client Portal widget
          </div>
          <div className="mb-6 text-caption text-neutral-125">
            Add this widget to your website and clients can access their portal
            with their email.
          </div>

          <div className="mb-8">
            <WidgetStylePicker
              selected={widgetStyle}
              onChange={setWidgetStyle}
            />
          </div>

          <div className="mb-8">
            <EmbedCodeSnippet
              providerId={providerId}
              style={widgetStyle}
              baseUrl={baseUrl}
            />
          </div>
        </div>

        <div className="min-w-[300px] flex-1">
          <WidgetPreview
            style={widgetStyle}
            primaryColor={primaryColor}
            providerName={providerName}
            squareLogoUrl={squareLogoUrl}
          />
        </div>
      </div>
    </div>
  );
};

export default ClientPortalSection;
